import { useEffect, useRef } from "react";
import { useTrans } from "../hooks";
import {
    Badge,
    Button,
    List,
    ListItem,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { AppIcon } from "./AppIcon";
import { useRecoilState } from "recoil";
import { MessageIdsAtom, MessageType } from "../atoms";
import { UserApi } from "../apis";
import { APPC, UTIL } from "../config";

const checkInterval = 10000;

export const AppMessageProcess = () => {
    const { t } = useTrans();
    const dayjs = UTIL.getDayJs();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [messages, setMessages] = useRecoilState(MessageIdsAtom);
    const messageIdsSetRef = useRef<Set<MessageType>>(new Set());

    const checkMessageIsComplete = async (message: MessageType) => {
        const { response } = await UserApi.getItemIsMessageComplete<{
            complete: boolean;
        }>(message.messageId);
        if (response && response.complete) {
            toast({
                title: t("cmn:message.process.completed", {
                    messageId: message.messageId,
                    type: t(`cmn:process.type.${message.type}`),
                }),
                status: "success",
            });
            setMessages((prev) =>
                prev.filter((m) => m.messageId !== message.messageId)
            );
            messageIdsSetRef.current.delete(message);
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            messageIdsSetRef.current.forEach((message) => {
                checkMessageIsComplete(message);
            });
        }, checkInterval);

        return () => {
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        messages.forEach((message) => {
            if (messageIdsSetRef.current.has(message)) {
                return;
            }
            messageIdsSetRef.current.add(message);
        });
    }, [messages]);

    if (messages.length < 1) {
        return <></>;
    }

    return (
        <Popover
            placement="bottom-end"
            isOpen={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            size={"xl"}
        >
            <PopoverTrigger>
                <Button
                    size={"md"}
                    className={"btn-icon-md"}
                    variant={"transparent"}
                >
                    <AppIcon
                        name={"ics-loop-circular"}
                        color={"var(--chakra-colors-success)"}
                        style={{ animation: "rotate 3s infinite" }}
                    />
                </Button>
            </PopoverTrigger>
            <PopoverContent minW={"340px"}>
                <PopoverBody>
                    {isOpen && (
                        <List spacing={4}>
                            {messages.map((m) => (
                                <ListItem
                                    key={m.messageId}
                                    textStyle={"regularXs"}
                                >
                                    <Badge size={"xs"} mr={2} variant={"solid"}>
                                        {dayjs(m?.createdAt).format(
                                            APPC.APP_DATEANDTIME_FORMAT
                                        )}
                                    </Badge>
                                    {t("cmn:message.process.processing", {
                                        messageId: m.messageId,
                                        type: t(`cmn:process.type.${m.type}`),
                                    })}
                                </ListItem>
                            ))}
                        </List>
                    )}
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};
